import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import PrimaryButton from '@components/PrimaryButton/PrimaryButton';
import { useTranslation } from 'react-i18next';

// Import images
import ClockImg from '@assets/images/ClockImg.png';
import ErrorImg from '@assets/images/ErrorImg.png'
import ThumbsUpImg from '@assets/images/ThumbsUpImg.png';
import {
  ButtonContainer,
  Container,
  Description,
  Icon,
  Title,
  CenteredContentContainer
} from './TemporaryTokenMigrationProcess.styles';
import { MigrationStatus, useMigrationStatus, isMigrationCompleted } from '@components/TemporaryTokenMigration/shared';
import { redirectToTelegramSupport } from '@lib/utils';
import LoadingModal from '@components/LoadingModal/LoadingModal';
import Snackbar from '@components/Snackbar/Snackbar';
import { safeAreaInset } from '@lib/telegram';
import { migrate } from '@components/TemporaryTokenMigration/migrationApi';

const TemporaryTokenMigrationProgress: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [isMigrating, setIsMigrating] = useState(false);
  const [isError, setIsError] = useState(false);

  // Poll every 3 seconds while on this page
  const { data, isLoading } = useMigrationStatus(3000)

  // Automatically redirect to home when migration is completed
  useEffect(() => {
    if (data?.status && isMigrationCompleted(data.status)) {
      navigate('/home', { replace: true });
    }
  }, [data?.status, navigate]);

  // Define content based on type
  const getContent = (status: MigrationStatus | undefined) => {
    switch (status) {
      case 'JETTON_RETURN_FAILED':
        return {
          icon: ErrorImg,
          title: t('common.tokenMigration.progress.returnFailed.title'),
          description: t('common.tokenMigration.progress.returnFailed.description'),
          buttonText: t('common.tokenMigration.progress.returnFailed.buttonText'),
          action: 'retry'
        }
      case 'SC_MINT_FAILED':
        return {
          icon: ErrorImg,
          title: t('common.tokenMigration.progress.mintFailed.title'),
          description: t('common.tokenMigration.progress.mintFailed.description'),
          buttonText: t('common.tokenMigration.progress.mintFailed.buttonText'),
          action: 'contact'
        }
      case 'COMPLETED_SC_MINT_SUCCESS':
        return {
          icon: ThumbsUpImg,
          title: t('common.tokenMigration.progress.completed.title'),
          description: t('common.tokenMigration.progress.completed.description'),
          buttonText: t('common.tokenMigration.progress.completed.buttonText'),
          action: 'home'
        }
      default:
        return {
          icon: ClockImg,
          title: t('common.tokenMigration.progress.processing.title'),
          description: t('common.tokenMigration.progress.processing.description'),
          buttonText: t('common.tokenMigration.progress.processing.buttonText'),
          action: 'home'
        }
    }
  }

  const content = getContent(data?.status);

  // Enable button only for failed states and completed state
  const isButtonEnabled =
    data?.status === 'JETTON_RETURN_FAILED' ||
    data?.status === 'SC_MINT_FAILED' ||
    data?.status === 'COMPLETED_SC_MINT_SUCCESS';

  const handleMigrate = async () => {
    setIsMigrating(true);
    setIsError(false);
    try {
      await migrate();
      // Stay on the same page, polling will detect status change
      setIsMigrating(false);
    } catch (error) {
      setIsMigrating(false);
      setIsError(true);
    }
  };

  const handleContactSupport = () => {
    redirectToTelegramSupport('JagadCS_bot');
    // Stay on the page (don't navigate away)
  };

  const handleButtonClick = () => {
    if (content.action === 'retry') {
      // Retry migration
      handleMigrate();
    } else if (content.action === 'contact') {
      // Open telegram support but stay on page
      handleContactSupport();
    } else if (content.action === 'home') {
      // Navigate to home (only enabled for COMPLETED_SC_MINT_SUCCESS)
      navigate('/home');
    }
  };

  const bottomInset = safeAreaInset().bottom || 24;

  return (
    <Container>
      <LoadingModal visible={isLoading || isMigrating} />
      <CenteredContentContainer>
        <Icon src={content.icon} alt="Status icon" />
        <Title>{content.title}</Title>
        <Description>{content.description}</Description>
      </CenteredContentContainer>

      <ButtonContainer $bottomInset={bottomInset}>
        <PrimaryButton
          onClick={handleButtonClick}
          variant="primary"
          disabled={!isButtonEnabled}
        >
          {content.buttonText}
        </PrimaryButton>
      </ButtonContainer>
      <Snackbar
        width='90%'
        isVisible={isError}
        duration={8000}
        message={t('errors.genericServerDescription')}
      />
    </Container>
  )
};

export default TemporaryTokenMigrationProgress;
